import { ChartNoAxesColumn, FileText, Home, Link2, LogOut, PanelLeftClose, PanelLeftOpen, Settings2, ShieldAlert, Workflow, KeyRound } from "lucide-react";
import { NavLink } from "react-router-dom";

const navItems = [
  { href: "/", icon: Home, label: "Home", end: true },
  { href: "/mrr-reporting", icon: ChartNoAxesColumn, label: "Reporting", end: false },
  { href: "/board-reports", icon: FileText, label: "Board Reports", end: false },
  { href: "/arr-sync", icon: Workflow, label: "ARR Sync", end: false },
  { href: "/connections", icon: Link2, label: "Connections", end: false },
  { href: "/settings", icon: Settings2, label: "Settings", end: false },
];

function SyncStatusDot({ status }) {
  if (status === "success") return <span className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-400" />;
  if (status === "running") return <span className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-blue-400" />;
  return <span className="h-2 w-2 flex-shrink-0 rounded-full bg-slate-600" />;
}

export default function AppSidebar({ authEmail, onLogout, onChangePassword, activeConnection, collapsed, onToggle, userRole, passwordRequestCount = 0 }) {
  const initial = (authEmail || "U")[0].toUpperCase();

  return (
    <aside
      style={{ width: collapsed ? 56 : 220 }}
      className="relative flex flex-shrink-0 flex-col overflow-hidden bg-[#323c44] transition-[width] duration-200 ease-in-out"
      data-testid="app-sidebar"
    >
      {/* ── Logo ── */}
      <div
        className={`flex items-center border-b border-white/[0.06] py-5 ${
          collapsed ? "justify-center px-0" : "gap-3 px-5"
        }`}
      >
        <svg viewBox="0 0 36 36" className="h-8 w-8 flex-shrink-0" aria-hidden="true">
          <rect width="36" height="36" rx="8" fill="#323c44" />
          <text
            x="18"
            y="25"
            fontFamily="system-ui,-apple-system,sans-serif"
            fontSize="13"
            fontWeight="800"
            textAnchor="middle"
          >
            <tspan fill="#50e3c2">P</tspan>
            <tspan fill="#ffffff">IQ</tspan>
          </text>
        </svg>
        {!collapsed && (
          <div className="min-w-0">
            <p className="font-sans text-[15px] font-bold leading-tight text-white">PortalIQ</p>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">
              Revenue Intelligence for your HubSpot Reporting
            </p>
          </div>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav
        className={`flex-1 space-y-0.5 py-4 ${collapsed ? "px-1.5" : "px-3"}`}
        data-testid="sidebar-navigation"
      >
        {!collapsed && (
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
            Workspace
          </p>
        )}
        {/* Platform admin nav — only visible to admin role */}
        {userRole === "admin" && (
          <NavLink
            to="/admin/users"
            title={collapsed ? "Platform Users" : undefined}
            className={({ isActive }) =>
              `group flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-150 ${
                collapsed ? "justify-center px-0" : "gap-3 px-3"
              } ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-200"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <ShieldAlert className={`h-4 w-4 flex-shrink-0 transition-colors duration-150 ${isActive ? "text-[#50e3c2]" : "text-slate-600 group-hover:text-slate-400"}`} />
                  {collapsed && passwordRequestCount > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-[#cfa77b]" aria-hidden="true" />
                  )}
                </span>
                {!collapsed && <span className="flex-1">Platform Users</span>}
                {/* People waiting for help with their password. */}
                {!collapsed && passwordRequestCount > 0 && (
                  <span
                    className="rounded-full bg-[#cfa77b] px-1.5 text-[11px] font-bold text-white"
                    title={`${passwordRequestCount} asking for help with their password`}
                    data-testid="password-request-badge"
                  >
                    {passwordRequestCount}
                  </span>
                )}
                {!collapsed && isActive && !passwordRequestCount && <span className="h-1.5 w-1.5 rounded-full bg-[#50e3c2]" aria-hidden="true" />}
              </>
            )}
          </NavLink>
        )}
        {navItems.map(({ href, icon: Icon, label, end }) => (
          <NavLink
            key={href}
            to={href}
            end={end}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `group flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-150 ${
                collapsed ? "justify-center px-0" : "gap-3 px-3"
              } ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-200"
              }`
            }
            data-testid={`sidebar-nav-${href.replace("/", "") || "home"}`}
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`h-4 w-4 flex-shrink-0 transition-colors duration-150 ${
                    isActive ? "text-[#50e3c2]" : "text-slate-600 group-hover:text-slate-400"
                  }`}
                />
                {!collapsed && <span className="flex-1">{label}</span>}
                {!collapsed && isActive && (
                  <span className="h-1.5 w-1.5 rounded-full bg-[#50e3c2]" aria-hidden="true" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* ── Connected portal pill ── */}
      <div className={`pb-3 ${collapsed ? "flex justify-center px-0" : "px-3"}`} data-testid="sidebar-portal-status">
        {collapsed ? (
          <SyncStatusDot status={activeConnection?.sync_status} />
        ) : (
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-3">
            <div className="flex items-center gap-2">
              <SyncStatusDot status={activeConnection?.sync_status} />
              <p className="truncate text-xs font-medium text-slate-300">
                {activeConnection?.portal_name || "No portal connected"}
              </p>
            </div>
            {activeConnection && (
              <p className="mt-1 truncate pl-4 text-[10px] text-slate-600">
                {activeConnection.last_sync_at
                  ? `Synced ${new Date(activeConnection.last_sync_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                  : "Awaiting first sync"}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── User ── */}
      <div
        className={`border-t border-white/[0.06] py-3 ${collapsed ? "px-1.5" : "px-3"}`}
        data-testid="sidebar-user"
      >
        {collapsed ? (
          <div className="flex justify-center py-1">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#50e3c2]/15 text-xs font-bold text-[#50e3c2]">
              {initial}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#50e3c2]/15 text-xs font-bold text-[#50e3c2]">
                {initial}
              </div>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-400">{authEmail}</span>
            </div>
            {onChangePassword && (
              <button
                type="button"
                onClick={onChangePassword}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 transition-all duration-150 hover:bg-white/[0.06] hover:text-slate-300"
                data-testid="change-password-button"
              >
                <KeyRound className="h-4 w-4" />
                Change password
              </button>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 transition-all duration-150 hover:bg-white/[0.06] hover:text-slate-300"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </>
        )}

        {/* ── Collapse / expand toggle ── */}
        <button
          type="button"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`mt-1 flex w-full items-center rounded-lg py-2 text-slate-600 transition-all duration-150 hover:bg-white/[0.06] hover:text-slate-300 ${
            collapsed ? "justify-center px-0" : "gap-3 px-3 text-sm"
          }`}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
